import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      
      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      
      // Retry failed requests up to 2 times
      retry: 2,
      
      // Don't refetch on window focus for better UX
      refetchOnWindowFocus: false,
      
      // Don't refetch on reconnect automatically
      refetchOnReconnect: false,
      
      // Background refetch interval (disabled by default)
      refetchInterval: false,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Query keys for consistent caching
export const queryKeys = {
  // Solutions
  solutions: ['solutions'] as const,
  solutionsList: (filters?: Record<string, unknown>) => ['solutions', 'list', filters] as const,
  solutionDetail: (id: string) => ['solutions', 'detail', id] as const,
  solutionsFeatured: ['solutions', 'featured'] as const,
  
  // Platforms
  platforms: ['platforms'] as const,
  platformsList: (filters?: Record<string, unknown>) => ['platforms', 'list', filters] as const,
  platformDetail: (id: string) => ['platforms', 'detail', id] as const,
  
  // Blog
  blogPosts: ['blog'] as const,
  blogPostsList: (filters?: Record<string, unknown>) => ['blog', 'list', filters] as const,
  blogPostDetail: (id: string) => ['blog', 'detail', id] as const,
  blogPostsFeatured: ['blog', 'featured'] as const,
  blogPostsLatest: ['blog', 'latest'] as const,
  
  // Partners
  partners: ['partners'] as const,
  
  // Insights / Statistics
  insights: ['insights'] as const,
  statistics: ['statistics'] as const,
  
  // Tags
  tagMappings: ['tags', 'mappings'] as const,
  
  // User / Auth
  profile: (userId: string) => ['profile', userId] as const,
  vendorSolutions: (userId: string) => ['vendor', 'solutions', userId] as const,
  vendorPlatforms: (userId: string) => ['vendor', 'platforms', userId] as const,
  
  // Admin
  adminSolutions: ['admin', 'solutions'] as const,
  adminPlatforms: ['admin', 'platforms'] as const,
  adminBlogPosts: ['admin', 'blog'] as const,
  adminPartners: ['admin', 'partners'] as const,
};

// Helper to invalidate related queries
export const invalidateQueries = {
  solutions: () => queryClient.invalidateQueries({ queryKey: queryKeys.solutions }),
  platforms: () => queryClient.invalidateQueries({ queryKey: queryKeys.platforms }),
  blog: () => queryClient.invalidateQueries({ queryKey: queryKeys.blogPosts }),
  partners: () => queryClient.invalidateQueries({ queryKey: queryKeys.partners }),
  insights: () => queryClient.invalidateQueries({ queryKey: queryKeys.insights }),
};
